import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";
import { uploadPublic } from "@/components/CustomCardForm";
import { CONDITIONS, condLabel } from "@/lib/conditions";

const fileUrl = (p) => (!p ? null : String(p).startsWith("http") ? p
  : String(p).startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${p}`);

// 3 segmentos: enviada · revisión · publicada. tone = color del estado.
const STATE = {
  EN_REVISION: { label: "EN REVISIÓN", tone: "#F59E0B", seg: 2, text: "Al aprobarla se publica con tus datos y te avisamos." },
  PUBLICADA: { label: "PUBLICADA", tone: "#34D399", seg: 3, text: "Aprobada y publicada automáticamente" },
  FALTA_DATO: { label: "FALTA 1 DATO", tone: "#FF6B5E", seg: 2 },
  APROBADA_FALTA_TU_DATO: { label: "APROBADA · FALTA TU DATO", tone: "#FF6B5E", seg: 3 },
  APROBADA_SIN_PUBLICAR: { label: "APROBADA", tone: "#FFFFFF", seg: 3, text: "Ya está en el catálogo. Completa precio y fotos" },
  RECHAZADA: { label: "RECHAZADA", tone: "#EF4444", seg: 2 },
};
const FIX_LABEL = { photos: "Cambiar foto", price: "Corregir precio", name: "Corregir nombre", set_number: "Corregir set / número", condition: "Cambiar condición" };
const CONDS = CONDITIONS.map((c) => c.value);

const Bar = ({ seg, tone }) => (
  <div className="flex gap-1 mt-2" aria-hidden>
    {[1, 2, 3].map((i) => <div key={i} className="h-1 flex-1 rounded-full" style={{ background: i <= seg ? tone : "#262626" }} />)}
  </div>
);

const FixForm = ({ it, onDone }) => {
  const field = it.needs_info?.field;
  const [v, setV] = useState({ price: it.pub_draft?.price || "", name: it.name_es || "", set_name: it.set_name || "", number: it.number || "", condition: it.pub_draft?.condition || "NM" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const body = { field };
      if (field === "photos") body.photo_paths = await Promise.all(photos.map(uploadPublic));
      if (field === "price") body.price = parseFloat(v.price);
      if (field === "name") body.name = v.name;
      if (field === "set_number") { body.set_name = v.set_name; body.number = v.number; }
      if (field === "condition") body.condition = v.condition;
      await api.patch(`/custom-cards/${it.id}/fix`, body);
      toast.success("Corregido · vuelve a revisión");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar la corrección");
    } finally { setBusy(false); }
  };
  const inp = "h-9 bg-[#171717] border border-[#262626] rounded px-2 text-sm w-full";
  return (
    <div className="mt-2 space-y-2" data-testid={`p7-fix-form-${it.id}`}>
      {field === "photos" && <MultiPhotoInput photos={photos} onChange={setPhotos} min={2} max={10} testid={`p7-fix-photos-${it.id}`} />}
      {field === "price" && <input type="number" min="0.25" step="0.01" value={v.price} onChange={(e) => setV({ ...v, price: e.target.value })} className={inp} data-testid={`p7-fix-price-${it.id}`} />}
      {field === "name" && <input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className={inp} data-testid={`p7-fix-name-${it.id}`} />}
      {field === "set_number" && (
        <div className="grid grid-cols-2 gap-2">
          <input value={v.set_name} onChange={(e) => setV({ ...v, set_name: e.target.value })} placeholder="Set" className={inp} />
          <input value={v.number} onChange={(e) => setV({ ...v, number: e.target.value })} placeholder="Número" className={inp} />
        </div>
      )}
      {field === "condition" && (
        <select value={v.condition} onChange={(e) => setV({ ...v, condition: e.target.value })} className={inp} data-testid={`p7-fix-condition-${it.id}`}>
          {CONDS.map((c) => <option key={c} value={c}>{condLabel(c)}</option>)}
        </select>
      )}
      <Button type="button" size="sm" disabled={busy || (field === "photos" && photos.length < 2)} onClick={send}
        className="h-8 bg-[#FF6B5E] hover:bg-[#ff5446] text-black" data-testid={`p7-fix-send-${it.id}`}>{busy ? "Enviando…" : "Enviar corrección"}</Button>
    </div>
  );
};

const Request = ({ it, onPublish, reload }) => {
  const nav = useNavigate();
  const [fixing, setFixing] = useState(false);
  const st = STATE[it.ui_status] || STATE.EN_REVISION;
  const img = fileUrl(it.image_url) || fileUrl(it.identification_photos?.[0]);
  const text = it.ui_status === "FALTA_DATO" ? it.needs_info?.reason
    : it.ui_status === "APROBADA_FALTA_TU_DATO" ? it.auto_publish_seller_issue
    : it.ui_status === "RECHAZADA" ? (it.reject_reason ? `Motivo: ${it.reject_reason}` : "Rechazada") : st.text;
  return (
    <li className="p-3 rounded-[14px] border border-[#262626] bg-[#0A0A0A]" data-testid={`p7-request-${it.id}`}>
      <div className="flex gap-3">
        <div className="w-12 h-16 shrink-0 rounded overflow-hidden bg-black grid place-items-center">
          {img ? <img src={img} alt="" className="w-full h-full object-contain" loading="lazy" /> : <span className="text-[9px] text-neutral-600">Sin foto</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{it.name_es || it.id}</div>
          <div className="text-[11px] text-neutral-500 truncate">{[it.set_name, it.number && `#${it.number}`, it.pub_draft?.price && `$${Number(it.pub_draft.price).toFixed(2)}`].filter(Boolean).join(" · ")}</div>
          <span className="inline-block mt-1.5 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full border" style={{ color: st.tone, borderColor: `${st.tone}80` }}
            data-testid={`p7-status-${it.id}`}>{st.label}</span>
          <Bar seg={st.seg} tone={st.tone} />
          {text && <div className="text-[11px] mt-1.5" style={{ color: st.tone === "#FFFFFF" ? "#d4d4d4" : st.tone }} data-testid={`p7-text-${it.id}`}>{text}</div>}
          <div className="mt-2 flex gap-2 flex-wrap">
            {it.ui_status === "PUBLICADA" && <Button type="button" size="sm" onClick={() => nav("/seller")} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid={`p7-view-${it.id}`}>Ver</Button>}
            {it.ui_status === "APROBADA_SIN_PUBLICAR" && <Button type="button" size="sm" onClick={() => onPublish(it)} className="h-7 text-[11px] bg-white text-black hover:bg-neutral-200" data-testid={`p7-publish-${it.id}`}>Publicar</Button>}
            {it.ui_status === "APROBADA_FALTA_TU_DATO" && <Button type="button" size="sm" onClick={() => nav("/seller?tab=payout")} className="h-7 text-[11px] bg-[#FF6B5E] text-black" data-testid={`p7-resolve-${it.id}`}>Agregar datos de cobro</Button>}
            {it.ui_status === "FALTA_DATO" && !fixing && <Button type="button" size="sm" onClick={() => setFixing(true)} className="h-7 text-[11px] bg-[#FF6B5E] text-black" data-testid={`p7-fix-${it.id}`}>{FIX_LABEL[it.needs_info?.field] || "Corregir"}</Button>}
          </div>
          {fixing && <FixForm it={it} onDone={() => { setFixing(false); reload(); }} />}
        </div>
      </div>
    </li>
  );
};

// P7 · Tus solicitudes
export const RequestsP7 = ({ items, onPublish, reload }) => (
  <ul className="space-y-2" data-testid="p7-list">
    {items.map((it) => <Request key={it.id} it={it} onPublish={onPublish} reload={reload} />)}
  </ul>
);
